import { useEffect } from "react";
import { useSession } from "./state/session.js";
import { useRoute, navigate } from "./router.js";
import { Login } from "./screens/Login.js";
import { Register } from "./screens/Register.js";
import { Shell } from "./screens/Shell.js";
import { ToastHost } from "./ui/Toast.js";
import { useT } from "./i18n/index.js";
import { onWs } from "./api/ws.js";
import { useBalances } from "./state/balances.js";
import { useGameAudio } from "./audio/useGameAudio.js";
import { useProgressionPushes } from "./base/useProgressionPushes.js";
import { HqCelebration } from "./base/HqCelebration.js";
import { useBase } from "./base/state.js";

export function App() {
  const status = useSession((s) => s.status);
  const settings = useSession((s) => s.settings);
  const load = useSession((s) => s.load);
  const route = useRoute();
  const t = useT();
  useGameAudio(status, settings, route);
  useProgressionPushes();

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const offCredits = onWs("balances.credits", (e) => {
      const p = e.payload as { balance: number; reason: string };
      useBalances.getState().applyCreditChange(p.balance, p.reason);
    });
    const offRes = onWs("balances.resources", (e) => {
      const p = e.payload as { deltas: Record<string, number>; collectionRef?: string; reason?: string };
      useBalances.getState().applyResourceDeltas(p.deltas, p.collectionRef);
      if (p.reason === "production.auto_collect") void useBase.getState().refresh();
    });
    const offItem = onWs("inventory.item", (e) => {
      const p = e.payload as { itemId: string; qty: number };
      useSession.setState((s) => ({ inventory: [...s.inventory.filter((item) => item.itemId !== p.itemId), ...(p.qty > 0 ? [{ itemId: p.itemId, qty: p.qty }] : [])] }));
    });
    return () => {
      offCredits();
      offRes();
      offItem();
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("reduced", !!settings?.reducedMotion);
  }, [settings?.reducedMotion]);

  useEffect(() => {
    if (status === "anon" && route.startsWith("/app")) navigate("/login", true);
    if (status === "authed" && (route === "/" || route === "/login" || route === "/register")) navigate("/app", true);
  }, [status, route]);

  let screen: React.ReactNode;
  if (status === "loading") screen = <Centered>{t("common.loading")}</Centered>;
  else if (status === "anon") screen = route === "/register" ? <Register /> : <Login />;
  else screen = <Shell route={route} />;

  return (
    <>
      {screen}
      <HqCelebration active={status === "authed" && route === "/app"} />
      <ToastHost />
    </>
  );
}

export function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100%", display: "grid", placeItems: "center", padding: 24 }} className="dim">
      {children}
    </div>
  );
}
